import React from 'react';

import { textGray900 } from '@osmosis/tailwind-ui/styles/text';
import { bg200 } from '@osmosis/tailwind-ui/styles/background';
import { Input } from '@osmosis/tailwind-ui/components/Input';
import { InputProps } from '@osmosis/tailwind-ui/components/Input';
import { Box } from '@osmosis/tailwind-ui/components/Box';
import { BoxProps } from '@osmosis/tailwind-ui/components/Box';

import InputValidationMessage from './InputValidationMessage';

type Props = {
  error?: boolean;
  errorMessage?: string;
  showErrorMessage?: boolean;
  inputProps?: InputProps;
  messageProps?: BoxProps;
  containerProps?: BoxProps;
  variant?: 'filled' | 'outlined' | string;
  htmlFor?: string;
  ariaLabel?: string;
  ariaLive?: 'off' | 'polite' | 'assertive';
};

const filled = (err?: boolean): string =>
  `outline-none rounded-none rounded-tl-[5px] rounded-tr-[5px] p-3 ${textGray900} ${bg200} border-b-2 ${
    err
      ? `border-red-200 focus:border-red-500`
      : 'border-gray-300 focus:border-blue-500'
  }`;

const outlined = (err?: boolean): string =>
  `outline-none rounded-md p-3 text-[#2c2c2c] bg-[#ebebeb] ${
    err ? 'border-[3px] border-[#E71111]' : ''
  } focus:border-[3px] focus:${err ? 'border-[#E71111]' : 'border-[#287CDC]'}`;

const variants = {
  filled,
  outlined,
} as { [variant: string]: (err?: boolean) => string };

/**
 * Returns InputValidation component.
 */
const InputValidation = (props: Props) => {
  const {
    error,
    errorMessage,
    inputProps = {},
    messageProps = {},
    containerProps = {},
    variant,
    htmlFor,
    ariaLabel,
    ariaLive,
  } = props;

  return (
    <Box {...containerProps}>
      <Input
        id={inputProps?.['data-testid'] || `${inputProps.name}-input`}
        aria-label={ariaLabel || htmlFor}
        aria-live={ariaLive}
        aria-invalid={error ? 'true' : 'false'}
        aria-describedby={error ? `${inputProps.name}-error` : undefined}
        {...inputProps}
        autoComplete={inputProps?.autoComplete || 'off'}
        data-testid={inputProps?.['data-testid'] || `${inputProps.name}-input`}
        cn={`${variant ? variants[variant]?.(error) : ''} ${
          error ? `placeholder:text-red-700 bg-red-100` : ''
        } ${inputProps?.cn || ''}`}
      />

      <InputValidationMessage
        error={error}
        errorMessage={errorMessage}
        messageProps={{ ...messageProps }}
      />
    </Box>
  );
};

export default InputValidation;
